import {useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction} from 'react'

import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'

import {clearPendingSsoOrgLogin, PENDING_SSO_ORG_STORAGE_KEY} from './pending-sso-org'
import {
  DEFAULT_TASK_COMPOSER_SCOPE,
  resolvePersistedScope,
  toPersistedScope,
  type AvailableOrganization,
  type TaskComposerScope,
} from './shared'

export interface TaskComposerScopeState {
  selectedScope: TaskComposerScope
  setSelectedScope: Dispatch<SetStateAction<TaskComposerScope>>
  setDerivedSelectedScope: Dispatch<SetStateAction<TaskComposerScope>>
  /** Owner preserved from a handoff when it cannot be represented by the picker. */
  launchScopeLogin?: string
  /** Retained owner that still requires SSO before repository-less submission. */
  blockedScopeLogin?: string
  /**
   * The org login an SSO redirect was started for, read once on mount.
   *
   * Callers that own the environment need it too: returning from the handshake
   * has to land back on Sandbox, which is the only place the scope applies.
   */
  pendingSsoOrgLogin: string | null
}

/**
 * Owns which organization a Sandbox draft is scoped to.
 *
 * It lives outside the task composer controller so a surface that switches
 * between destinations can keep one scope: the picker is offered for a Sandbox
 * Chat exactly as it is for a Sandbox task, and moving between them must not
 * silently reset the owner the repository picker filters by.
 *
 * The scope also persists to `copilotLocalStorage` so a returning user lands
 * on the org they last picked. Persistence is only meaningful when the composer
 * can reach Sandbox, since that's the only surface where the scope applies.
 * Callers pass `enablePersistence=false` (for example, actions-only surfaces
 * that never show the ScopePicker) to skip both the initial read and the write
 * effect. A caller that supplies its own scope passes an empty
 * `availableOrganizations` list; that also suppresses persistence here so a
 * secondary composer instance can't clobber the primary selection.
 *
 * `initialScopeLogin` seeds the scope from a handoff (for example, the owner
 * from `?repo=owner/name`). It resolves to personal when it matches
 * `currentUserLogin`, to an org when it matches an available org, and
 * otherwise falls through to persisted scope.
 */
export function useTaskComposerScope(
  availableOrganizations: AvailableOrganization[],
  enablePersistence: boolean,
  initialScopeLogin?: string,
  currentUserLogin?: string,
  lastContributedOrganizationLogin?: string,
): TaskComposerScopeState {
  const persist = availableOrganizations.length > 0 && enablePersistence
  // After returning from an SSO redirect kicked off by the ScopePicker, restore
  // the scope to the org the user had picked and the Sandbox environment they
  // were on. The sessionStorage read is inlined here (rather than behind a
  // helper) because the linter rejects hidden non-reactive reads during
  // render. The value is only cleared after commit so a StrictMode-abandoned
  // first render can't swallow the one-shot handshake.
  const [pendingSsoOrgLogin] = useState(() => {
    try {
      return sessionStorage.getItem(PENDING_SSO_ORG_STORAGE_KEY)
    } catch {
      return null
    }
  })
  const restoredSsoOrg = useMemo(
    () =>
      pendingSsoOrgLogin
        ? (availableOrganizations.find(org => org.login === pendingSsoOrgLogin && !org.requiresSso) ?? null)
        : null,
    [pendingSsoOrgLogin, availableOrganizations],
  )
  const initialScopeMatchesPersonal = useMemo(
    () =>
      Boolean(
        initialScopeLogin && currentUserLogin && initialScopeLogin.toLowerCase() === currentUserLogin.toLowerCase(),
      ),
    [currentUserLogin, initialScopeLogin],
  )
  const initialOrganization = useMemo(
    () =>
      initialScopeLogin && !initialScopeMatchesPersonal
        ? (availableOrganizations.find(org => org.login.toLowerCase() === initialScopeLogin.toLowerCase()) ?? null)
        : null,
    [availableOrganizations, initialScopeLogin, initialScopeMatchesPersonal],
  )
  const defaultOrganization = useMemo(
    () =>
      lastContributedOrganizationLogin
        ? (availableOrganizations.find(
            org => org.login.toLowerCase() === lastContributedOrganizationLogin.toLowerCase() && !org.requiresSso,
          ) ?? null)
        : null,
    [availableOrganizations, lastContributedOrganizationLogin],
  )
  const [selectedScope, setSelectedScope] = useState<TaskComposerScope>(() => {
    if (restoredSsoOrg) return {kind: 'organization', organization: restoredSsoOrg}
    if (initialScopeMatchesPersonal) return {kind: 'personal'}
    if (initialOrganization) return {kind: 'organization', organization: initialOrganization}
    if (persist) {
      const persisted = copilotLocalStorage.getTaskComposerScope()
      if (persisted) return resolvePersistedScope(persisted, availableOrganizations)
      // A null read means "never chose" or "chose over a day ago"; only the no-TTL
      // marker separates them, and an explicit choice must keep beating the default.
      if (
        !pendingSsoOrgLogin &&
        !copilotLocalStorage.hasChosenTaskComposerScope(currentUserLogin) &&
        defaultOrganization
      ) {
        return {kind: 'organization', organization: defaultOrganization}
      }
    }
    return DEFAULT_TASK_COMPOSER_SCOPE
  })
  useEffect(() => {
    if (!pendingSsoOrgLogin) return
    if (restoredSsoOrg && enablePersistence) {
      copilotLocalStorage.setTaskComposerScopeChosen(currentUserLogin)
    }
    clearPendingSsoOrgLogin()
    // Only clear once, after commit — the pending value is a one-shot handshake.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // If `availableOrganizations` changes shape after mount (e.g. the payload
  // was populated lazily), re-validate the selected scope. Downgrade to
  // personal only when the current org is no longer available; keep an
  // SSO-required org so the composer can drive the handshake, and never
  // re-promote to an org the user did not just select.
  useEffect(() => {
    if (selectedScope.kind !== 'organization') return
    const match = availableOrganizations.find(org => org.login === selectedScope.organization.login)
    if (!match) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedScope({kind: 'personal'})
    }
  }, [availableOrganizations, selectedScope])

  // Only persist an explicit choice, so an absent server field is a true no-op
  // and withdrawing the field takes effect on the next mount.
  useEffect(() => {
    if (!persist) return
    if (!copilotLocalStorage.hasChosenTaskComposerScope(currentUserLogin)) return
    copilotLocalStorage.setTaskComposerScope(toPersistedScope(selectedScope))
  }, [currentUserLogin, persist, selectedScope])

  const selectScope = useCallback<Dispatch<SetStateAction<TaskComposerScope>>>(
    next => {
      if (enablePersistence) copilotLocalStorage.setTaskComposerScopeChosen(currentUserLogin)
      setSelectedScope(next)
    },
    [currentUserLogin, enablePersistence],
  )

  return {
    selectedScope,
    setSelectedScope: selectScope,
    setDerivedSelectedScope: setSelectedScope,
    pendingSsoOrgLogin,
  }
}
